/**
 * Marker Valuation Service (studio)
 *
 * Pulls LIVE KRO marker valuations from the KRO backend oracle
 * (GET /api/v1/marker-valuations), which reads on-chain supply from Provenance
 * mainnet and multiplies by the institutional unit price. Dashboards use the
 * useMarkerValuations() hook to auto-refresh without manual reloads.
 *
 * If the API is unreachable, callers fall back to MARKER_VALUATION_FALLBACK so
 * the UI always renders sane numbers (last ZoneScan-verified supply).
 */
import { useEffect, useRef, useState } from 'react';

const RAW_BASE = process.env.REACT_APP_KRO_API_BASE || 'https://api.kromarketplace.com';
// Components disagree on whether the env value already includes /api/v1 — normalize.
const API_BASE = RAW_BASE.replace(/\/+$/, '').replace(/\/api\/v1$/, '');
export const MARKER_VALUATIONS_ENDPOINT = `${API_BASE}/api/v1/marker-valuations`;

// Last ZoneScan-verified values — used only when the live API is unreachable.
export const MARKER_VALUATION_FALLBACK = {
  generated_at: null,
  supply_source: 'fallback',
  portfolio_summary: { total_markers: 254, total_value_usd: 1_210_000_000 },
  markers: {
    'kro.gipr.genesis.v2':      { denom: 'kro.gipr.genesis.v2',      units: 60,  price_per_unit_usd: 1_000_000,  total_value_usd: 60_000_000 },
    'kro.as.accountability.v2': { denom: 'kro.as.accountability.v2', units: 182, price_per_unit_usd: 5_000_000,  total_value_usd: 910_000_000 },
    'kro.sc.stewardship.v2':    { denom: 'kro.sc.stewardship.v2',    units: 12,  price_per_unit_usd: 20_000_000, total_value_usd: 240_000_000 },
  },
};

export async function fetchMarkerValuations(signal) {
  const res = await fetch(MARKER_VALUATIONS_ENDPOINT, { signal });
  if (!res.ok) throw new Error(`marker-valuations ${res.status}`);
  return res.json();
}

/**
 * React hook: live marker valuations with auto-refresh.
 * @param {number} intervalMs  poll interval (default 60s); pass 0 to disable polling.
 * @returns {{ data, markers, loading, error, isLive, refreshedAt, refresh }}
 */
export function useMarkerValuations(intervalMs = 60_000) {
  const [data, setData] = useState(MARKER_VALUATION_FALLBACK);
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const load = async (signal) => {
    try {
      const json = await fetchMarkerValuations(signal);
      setData(json);
      setIsLive(true);
      setError(null);
    } catch (e) {
      if (e.name !== 'AbortError') { setIsLive(false); setError(e.message); }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    if (intervalMs > 0) {
      timer.current = setInterval(() => load(ctrl.signal), intervalMs);
    }
    return () => { ctrl.abort(); if (timer.current) clearInterval(timer.current); };
  }, [intervalMs]);

  return {
    data,
    markers: data?.markers || {},
    loading,
    error,
    isLive,
    refreshedAt: data?.generated_at || null,
    refresh: () => load(),
  };
}
